import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import LocationSelector from "../../components/location/LocationSelector";
import QRScanner from "../../utils/QRScanner";

import logo from "./assets/logo.svg";

const Navbar: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isMenuOpen, setMenuOpen] = useState(false);

    const [isScannerOpen, setScannerOpen] = useState(false);
    const isAuthenticated = !!localStorage.getItem("saca_token");

    const closeMenu = useCallback(() => {
        setMenuOpen(false);
    }, []);

    const toggleMenu = useCallback(() => {
        setMenuOpen((prev) => !prev);
    }, []);

    const scrollToSection = useCallback((sectionId: string) => {
        if (!sectionId || typeof globalThis === "undefined") {
            return;
        }
        globalThis.window.requestAnimationFrame(() => {
            const section = document.getElementById(sectionId);
            if (section) {
                section.scrollIntoView({ behavior: "smooth" });
            }
        });
    }, []);

    const handlePitchesClick = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault();
            closeMenu();
            if (location.pathname === "/") {
                scrollToSection("pitch-section");
                return;
            }
            navigate("/pitches");
        },
        [closeMenu, location.pathname, navigate, scrollToSection]
    );

    const handleMatchesClick = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault();
            closeMenu();
            if (location.pathname === "/") {
                scrollToSection("match-section");
                return;
            }
            navigate("/matches");
        },
        [closeMenu, location.pathname, navigate, scrollToSection]
    );

    const handleLeaguesClick = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault();
            closeMenu();
            if (location.pathname === "/") {
                scrollToSection("leagues-section");
                return;
            }
            navigate("/leagues");
        },
        [closeMenu, location.pathname, navigate, scrollToSection]
    );

    const handleTournamentClick = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault();
            closeMenu();
            navigate("/", {
                state: {
                    activeTab: "tournaments",
                    scrollTo: "tournaments-section",
                },
            });
        },
        [closeMenu, navigate]
    );

    const closeScanner = useCallback(() => {
        setScannerOpen(false);
    }, []);

    const handleScan = useCallback(
        (data: unknown) => {
            if (data) {
                closeScanner();
            }
        },
        [closeScanner]
    );





    useEffect(() => {
        if (!isMenuOpen) {
            return undefined;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                closeMenu();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [closeMenu, isMenuOpen]);

    useEffect(() => {
        document.body.style.overflow = isMenuOpen ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [isMenuOpen]);

    useEffect(() => {
        closeMenu();
    }, [closeMenu, location.pathname]);

    return (
        <>
            {/* QR Scanner Modal */}
            {isScannerOpen && (
                <QRScanner closeScanner={closeScanner} onScan={handleScan} />
            )}
            <div
                className={`w-full bg-[#000000a2] backdrop-blur-xl h-[500px] p-6 flex justify-center items-center shadow transition-all duration-500 top-[78px] fixed z-[60] ${isMenuOpen
                    ? "translate-y-0 opacity-100"
                    : "-translate-y-[600px] opacity-0 pointer-events-none"
                    }`}
                aria-hidden={!isMenuOpen}
            >
                <div className="w-full flex flex-col justify-center items-center space-y-5">
                    <LocationSelector variant="pill" />
                    <Link to="/" className=" text-white font-Manrope font-medium text-sm">
                        Home
                    </Link>
                    <Link
                        to="/pitches"
                        onClick={handlePitchesClick}
                        className="text-white font-Manrope font-medium text-sm"
                    >
                        Available Pitches
                    </Link>
                    <Link
                        to="/matches"
                        onClick={handleMatchesClick}
                        className=" text-white font-Manrope font-medium text-sm"
                    >
                        Matches
                    </Link>
                    <Link
                        to="/charity"
                        className=" text-white font-Manrope font-medium text-sm"
                    >
                        Charity
                    </Link>
                    <Link
                        to="/leagues"
                        onClick={handleLeaguesClick}
                        className=" text-white font-Manrope font-medium text-sm"
                    >
                        Leagues
                    </Link>
                    <Link
                        to="/contact"
                        className=" text-white font-Manrope font-medium text-sm"
                    >
                        Contact us
                    </Link>
                    <Link
                        to="/events"
                        className=" text-white font-Manrope font-medium text-sm"
                    >
                        Events
                    </Link>
                    <div className="flex md:hidden space-x-3 z-50 items-center mt-6">
                        {isAuthenticated ? (
                            <Link
                                to="/dashboard"
                                onClick={closeMenu}
                                className="px-6 py-[14px] bg-[#317333] rounded-[8px] font-Manrope font-semibold text-sm text-white"
                            >
                                Go to Dashboard
                            </Link>
                        ) : (
                            <>
                                <Link
                                    to="/sign-up"
                                    onClick={closeMenu}
                                    className="px-6 py-[14px] text-[#317333] font-Manrope font-semibold text-sm"
                                >
                                    Sign Up
                                </Link>
                                <Link
                                    to="/sign-in"
                                    onClick={closeMenu}
                                    className="px-6 py-[14px] bg-[#317333] rounded-[8px] font-Manrope font-semibold text-sm text-white"
                                >
                                    Log In
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <nav className=" py-5 px-4 md:px-14 w-full flex items-center justify-between absolute top-0 left-0 z-[99]">
                <Link to="/">
                    <img src={logo} alt="SACA logo" />
                </Link>
                <div className=" hidden lg:flex items-center space-x-6">
                    <Link to="/" className=" text-white font-Manrope font-medium text-sm">
                        Home
                    </Link>
                    <Link
                        to="/pitches"
                        onClick={handlePitchesClick}
                        className=" text-white font-Manrope font-medium text-sm"
                    >
                        Pitches
                    </Link>
                    <Link
                        to="/matches"
                        onClick={handleMatchesClick}
                        className="text-white font-Manrope font-medium text-sm"
                    >
                        Matches
                    </Link>

                    <Link
                        to="/"
                        onClick={handleTournamentClick}
                        className="text-white font-Manrope font-medium text-sm"
                    >
                        Tournaments
                    </Link>
                    <Link
                        to="/charity"
                        className=" text-white font-Manrope font-medium text-sm"
                    >
                        Charity
                    </Link>
                    <Link
                        to="/contact"
                        className=" text-white font-Manrope font-medium text-sm"
                    >
                        Contact us
                    </Link>
                </div>

                <div className=" flex items-center space-x-5">
                    <LocationSelector
                        variant="button"
                        className="hidden md:inline-flex"
                    />
                    <div className="hidden md:flex items-center space-x-3">
                        {isAuthenticated ? (
                            <Link
                                to="/dashboard"
                                className="px-6 py-[14px] bg-[#317333] rounded-[8px] font-Manrope font-semibold text-sm text-white"
                            >
                                Go to Dashboard
                            </Link>
                        ) : (
                            <>
                                <Link
                                    to="/sign-up"
                                    onClick={closeMenu}
                                    className="px-6 py-[14px] text-[#317333] font-Manrope font-semibold text-sm bg-white rounded-[6px]"
                                >
                                    Sign up
                                </Link>
                                <Link
                                    to="/sign-in"
                                    onClick={closeMenu}
                                    className="px-6 py-[14px] bg-[#317333] rounded-[8px] font-Manrope font-semibold text-sm text-white"
                                >
                                    Log In
                                </Link>
                            </>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={toggleMenu}
                        className={`menu-icon w-8 h-8 rounded-full bg-[#ffffff5b] md:block lg:hidden ${isMenuOpen ? "active" : ""
                            }`}
                        aria-expanded={isMenuOpen}
                        aria-label="Toggle navigation"
                    >
                        <div className="flex items-center justify-center w-full h-full">
                            <span></span>
                            <span></span>
                        </div>
                    </button>
                </div>
            </nav>
        </>
    );
};

export default Navbar;
